/**
 * Shared channel/chart search matcher: whitespace-separated tokens, ALL of which must match (AND),
 * each token tested against every searchable field of a candidate.
 *
 * Back-compat contract (non-negotiable): a token with no `*` is a case-insensitive SUBSTRING test,
 * exactly as every channel search in the app behaved before this module existed. Tokens are
 * produced by splitting on whitespace, so no token can contain a space; the old haystack was joined
 * with " ". A substring occurrence of a token in that joined string therefore always lies entirely
 * within one field — it can never straddle a join boundary. So `fields.join(" ").includes(token)`
 * is *identical* to `fields.some(f => f.includes(token))`. Per-field matching is not an
 * approximation of the old behaviour; it is the old behaviour, and it is what lets `*` anchor to a
 * single field instead of to a meaningless concatenation.
 *
 * A token containing `*` becomes an ANCHORED glob over one field: `bsp*` is "starts with bsp",
 * `*_kts` is "ends with _kts", `*bsp*` is plain substring. There is deliberately no fallback to
 * substring when the anchored form fails — otherwise `*` would be decorative.
 *
 * `?` is NOT a wildcard; it is matched literally. Making it one would force any token containing a
 * `?` to become anchored, silently changing the meaning of existing queries, and `?` is ordinary
 * punctuation in data-dictionary descriptions. If it is ever added it must map to `.` and stay
 * subject to the same "only `*` triggers anchoring" rule.
 */

import { normalizeChannelNameForMatch } from './channelRef';

/**
 * 20 by user request: a short list, and the user types more characters to narrow it. The cap is
 * never silent — callers pair it with a truthful "showing N of M — narrow your search".
 */
export const CHANNEL_SEARCH_RESULT_LIMIT = 20;

type CompiledTerm =
  | { kind: 'any' }
  | { kind: 'substring'; needle: string }
  | { kind: 'prefix'; needle: string }
  | { kind: 'suffix'; needle: string }
  | { kind: 'glob'; re: RegExp };

export type PreparedChannelFields = readonly string[];

export type ChannelSearchQuery = {
  /** Every term must match (AND) — narrowing is what a search box is for. */
  terms: CompiledTerm[];
  /** No usable terms: callers keep whatever they already do for an empty query. */
  isEmpty: boolean;
  /** At least one term used `*`. For UI hints only; matching never branches on it. */
  hasWildcard: boolean;
};

export type ChannelSearchResult<T> = {
  items: T[];
  /** TRUE match count, counted before any cap — so a truncation hint cannot lie. */
  total: number;
  truncated: boolean;
};

/** Real channel names contain `.`, `(`, `[`, `+`, `-` — every metacharacter must stay literal. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function compileTerm(token: string): CompiledTerm {
  if (!token.includes('*')) {return { kind: 'substring', needle: token };}

  const t = token.replace(/\*{2,}/g, '*');
  if (t === '*') {return { kind: 'any' };}

  const lead = t.startsWith('*');
  const tail = t.endsWith('*');
  const core = t.slice(lead ? 1 : 0, tail ? t.length - 1 : t.length);

  // The three shapes that cover nearly every real query, answered without a regex engine.
  if ((lead || tail) && !core.includes('*')) {
    if (lead && tail) {return { kind: 'substring', needle: core };}
    if (lead) {return { kind: 'suffix', needle: core };}
    return { kind: 'prefix', needle: core };
  }

  // Interior stars: literals escaped, `*` -> `.*`, anchored. No `i` flag — both sides are already
  // folded to lowercase. No nested quantifiers are constructible here, so no backtracking cliff.
  const source = t.split('*').map(escapeRegExp).join('.*');
  return { kind: 'glob', re: new RegExp(`^${source}$`) };
}

/**
 * Compile ONCE per query, then test many candidates against the result. Memoise at the call site
 * (createMemo) so a keystroke costs one compile, not one compile per channel.
 */
export function compileChannelSearch(query: string): ChannelSearchQuery {
  const terms: CompiledTerm[] = [];
  let hasWildcard = false;

  for (const raw of String(query ?? '').split(/\s+/)) {
    // The identity fold is the ONE normalisation — lowercase, trim, dots to underscores — applied
    // to the query here and to every field in prepareChannelFields. `*` passes through it
    // untouched, so folding cannot disturb the wildcard structure.
    const token = normalizeChannelNameForMatch(raw);
    if (!token) {continue;}
    if (token.includes('*')) {hasWildcard = true;}
    terms.push(compileTerm(token));
  }

  return { terms, isEmpty: terms.length === 0, hasWildcard };
}

/**
 * Fold a candidate's searchable fields once. Empty fields are dropped — they can never satisfy a
 * term, and carrying them would cost a comparison per term per candidate.
 */
export function prepareChannelFields(
  fields: ReadonlyArray<string | null | undefined>
): PreparedChannelFields {
  const out: string[] = [];
  for (const field of fields) {
    const folded = normalizeChannelNameForMatch(field ?? '');
    if (folded) {out.push(folded);}
  }
  return out;
}

function termMatchesField(term: CompiledTerm, field: string): boolean {
  switch (term.kind) {
    case 'any': return true;
    case 'substring': return field.includes(term.needle);
    case 'prefix': return field.startsWith(term.needle);
    case 'suffix': return field.endsWith(term.needle);
    case 'glob': return term.re.test(field);
  }
}

/** A term matches the candidate if ANY field satisfies it; the candidate matches if ALL terms do. */
export function matchesPreparedFields(
  fields: PreparedChannelFields,
  q: ChannelSearchQuery
): boolean {
  if (q.isEmpty) {return true;}
  for (const term of q.terms) {
    // `*` matches a candidate even when it has no non-empty field at all.
    if (term.kind === 'any') {continue;}
    let hit = false;
    for (const field of fields) {
      if (termMatchesField(term, field)) {hit = true; break;}
    }
    if (!hit) {return false;}
  }
  return true;
}

/** Convenience for cold paths: folds the fields inline. Hot loops should hoist the fold instead. */
export function matchesChannelSearch(
  fields: ReadonlyArray<string | null | undefined>,
  q: ChannelSearchQuery
): boolean {
  return matchesPreparedFields(prepareChannelFields(fields), q);
}

/**
 * Filter with an optional display cap. `total` is the TRUE match count — every candidate is still
 * tested past the cap, because a truncation hint that lies is worse than no hint. Input order is
 * preserved: the pickers' catalog order is meaningful and reranking would be a separate decision.
 */
export function filterChannelSearch<T>(
  items: readonly T[],
  query: string | ChannelSearchQuery,
  fieldsOf: (item: T) => PreparedChannelFields,
  limit: number = Infinity
): ChannelSearchResult<T> {
  const q = typeof query === 'string' ? compileChannelSearch(query) : query;
  const out: T[] = [];
  let total = 0;
  for (const item of items) {
    if (!matchesPreparedFields(fieldsOf(item), q)) {continue;}
    total += 1;
    if (out.length < limit) {out.push(item);}
  }
  return { items: out, total, truncated: total > out.length };
}
